import is from '@sindresorhus/is';
import noop from 'lodash/noop';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AgentChatMessage, ChatBlock, UserMessage } from '../../../data-contract/chat';
import { type ChatStream, ChatStreamRejectedError } from '../../../fetchers/chat/chat-fetchers';
import { CHAT_AUTO_CLOSE_AFTER_COMPLETE_MS } from '../../constants';
import { useChatStore } from '../../stores/chat-open-store';
import { useSendChatMessage } from '../api';
import {
    appendContextRefreshEvent,
    appendStructuredEvent,
    appendTextChunk,
    prependPendingAssistantMessage,
    prependUserMessage,
    reconcileAssistantMessageStart,
    removeAssistantMessage,
    setAssistantMessageStatus,
} from '../api/messages-cache';
import { useChatMessagesCache } from '../api/use-chat-messages-cache';

export type TurnStatus = 'complete' | 'error' | 'idle' | 'streaming';

/** `busy`: another stream still holds the thread's run lock, so the reply is in progress, not failed. */
export type TurnErrorReason = 'busy' | 'connection-lost' | 'rejected' | 'server';

export type ChatTurnState = {
    readonly status: TurnStatus;
    readonly messageId: string | null;
    readonly errorReason: TurnErrorReason | null;
};

type ThreadStreamState = ChatTurnState & {
    readonly hasLiveStream: boolean;
};

type StreamEntry = {
    autoCloseTimer: ReturnType<typeof setTimeout> | null;
    /** Epoch ms until which the server reported the thread's run lock as held. */
    busyUntil: number | null;
    busyRetryTimer: ReturnType<typeof setTimeout> | null;
    /** Consecutive busy answers since the last accepted stream; doubles the fallback retry delay. */
    busyStreak: number;
    currentMessageId: string | null;
    isComplete: boolean;
    isAgentActionTurn: boolean;
    lastEventId: string | null;
    lastRequest: UserMessage | null;
    optimisticMessageId: string | null;
    reconnectAttempts: number;
    reconnectTimer: ReturnType<typeof setTimeout> | null;
    sawLiveContextRefresh: boolean;
    stream: ChatStream | null;
};

export type ChatStreamRegistry = {
    readonly acceptAutoClose: () => void;
    readonly autoCloseScheduleId: number | null;
    readonly appendUserMessage: (threadId: string, message: AgentChatMessage) => void;
    readonly cancelAutoClose: () => void;
    readonly clearTurn: (threadId: string | null) => void;
    /**
     * Prepends an optimistic pending assistant message (reusing an unreconciled one) and marks
     * the turn streaming. `settleMessageId` marks the message whose question was just answered
     * as complete, so it stops spinning next to the placeholder.
     */
    readonly ensurePendingMessage: (threadId: string, settleMessageId?: string | null) => string;
    readonly getTurn: (threadId: string | null) => ChatTurnState;
    readonly hasLiveStream: (threadId: string | null) => boolean;
    readonly isAutoClosePending: boolean;
    readonly keepChatOpen: () => void;
    /**
     * Removes an optimistic message after a failed submit and restores the turn unless a stream
     * went live. `unsettleMessageId` reverts the message settled by `ensurePendingMessage`.
     */
    readonly rollbackPendingMessage: (
        threadId: string,
        messageId: string,
        previousTurn: ChatTurnState,
        unsettleMessageId?: string | null,
    ) => void;
    /** Replaces the failed reply `failedMessageId` with a new attempt at the same request. */
    readonly retryTurn: (threadId: string, failedMessageId: string) => void;
    readonly startTurn: (threadId: string, body: UserMessage, messageId?: string | null) => void;
    /**
     * Returns whether this ended a turn that was still live — streaming or awaiting a reconnect.
     * A turn that already finished, errored, or exhausted its reconnects (which reconciles itself)
     * has nothing left to stop, so the caller can skip a needless refetch.
     */
    readonly stopTurn: (threadId: string | null) => boolean;
};

export const MAX_RECONNECT_ATTEMPTS = 3;
export const RECONNECT_BASE_DELAY_MS = 500;
const HTTP_TOO_MANY_REQUESTS = 429;
const MS_PER_SECOND = 1000;
const DEFAULT_BUSY_RETRY_AFTER_SECONDS = 30;
const MAX_BUSY_RETRY_AFTER_SECONDS = 300;

const busyRetryDelayMs = (retryAfterSeconds: number | null, busyStreak: number): number => {
    const fallbackSeconds = DEFAULT_BUSY_RETRY_AFTER_SECONDS * 2 ** Math.max(busyStreak - 1, 0);
    const seconds = Math.min(Math.max(retryAfterSeconds ?? fallbackSeconds, 1), MAX_BUSY_RETRY_AFTER_SECONDS);
    return seconds * MS_PER_SECOND;
};

export const generateLocalMessageId = (): string =>
    is.function(crypto.randomUUID) ? crypto.randomUUID() : `local-${Date.now()}-${Math.random()}`;

export const INITIAL_TURN: ChatTurnState = { errorReason: null, messageId: null, status: 'idle' };

const INITIAL_THREAD_STREAM_STATE: ThreadStreamState = {
    ...INITIAL_TURN,
    hasLiveStream: false,
};

// Input controls (Accept/Reject) render only for the latest assistant message.
const requestsInputNow = (block: ChatBlock): boolean => {
    if (block.type === 'clarify-options') return true;
    if (block.type === 'change-proposal' || block.type === 'questionnaire') return block.mode === 'Review';
    return false;
};

// A dropped stream leaves the turn running or resumable on the server, so it is picked up again.
// A rejected or busy request never started, so it is sent again. Any other failure ended a stored
// request, which the server re-runs without storing it twice.
export const buildRetryRequest = (
    errorReason: TurnErrorReason | null,
    lastRequest: UserMessage | null,
): UserMessage => {
    if (errorReason === 'connection-lost') return { resume: true, type: 'resume' };
    if (is.null(lastRequest) || lastRequest.type === 'resume') return { type: 'retry' };
    if (errorReason === 'rejected' || errorReason === 'busy') return lastRequest;
    return is.undefined(lastRequest.context) ? { type: 'retry' } : { context: lastRequest.context, type: 'retry' };
};

const getInitialEntry = (): StreamEntry => ({
    autoCloseTimer: null,
    busyRetryTimer: null,
    busyStreak: 0,
    busyUntil: null,
    currentMessageId: null,
    isComplete: false,
    isAgentActionTurn: false,
    lastEventId: null,
    lastRequest: null,
    optimisticMessageId: null,
    reconnectAttempts: 0,
    reconnectTimer: null,
    sawLiveContextRefresh: false,
    stream: null,
});

export const useChatStreamRegistry = (activeThreadId: string | null): ChatStreamRegistry => {
    const { send } = useSendChatMessage();
    const { reconcileThreadAndList, updateMessages } = useChatMessagesCache();
    const activeThreadIdRef = useRef(activeThreadId);
    activeThreadIdRef.current = activeThreadId;
    const entriesRef = useRef<Map<string, StreamEntry>>(new Map());
    const nextAutoCloseScheduleIdRef = useRef(0);
    const [autoCloseScheduleId, setAutoCloseScheduleId] = useState<number | null>(null);
    const [statesByThreadId, setStatesByThreadId] = useState<Record<string, ThreadStreamState>>({});
    const isAutoClosePending = !is.null(autoCloseScheduleId);

    const getOrCreateEntry = useCallback((threadId: string): StreamEntry => {
        const existing = entriesRef.current.get(threadId);
        if (!is.undefined(existing)) return existing;
        const entry = getInitialEntry();
        entriesRef.current.set(threadId, entry);
        return entry;
    }, []);

    const clearBusyRetryTimer = useCallback((entry: StreamEntry): void => {
        if (!is.null(entry.busyRetryTimer)) {
            clearTimeout(entry.busyRetryTimer);
            entry.busyRetryTimer = null;
        }
    }, []);

    const clearReconnectTimer = useCallback((entry: StreamEntry): void => {
        if (!is.null(entry.reconnectTimer)) {
            clearTimeout(entry.reconnectTimer);
            entry.reconnectTimer = null;
        }
    }, []);

    const clearAutoCloseTimer = useCallback((entry: StreamEntry): void => {
        if (!is.null(entry.autoCloseTimer)) {
            clearTimeout(entry.autoCloseTimer);
            entry.autoCloseTimer = null;
        }
    }, []);

    // The chat dock is shared across threads, so a pending close armed by any
    // thread must not survive the user engaging again — on whichever thread.
    const clearAllAutoCloseTimers = useCallback((): void => {
        for (const entry of entriesRef.current.values()) {
            clearAutoCloseTimer(entry);
        }
        setAutoCloseScheduleId(null);
    }, [clearAutoCloseTimer]);

    const scheduleAutoClose = useCallback(
        (entry: StreamEntry): void => {
            clearAllAutoCloseTimers();
            const scheduleId = nextAutoCloseScheduleIdRef.current + 1;
            nextAutoCloseScheduleIdRef.current = scheduleId;
            setAutoCloseScheduleId(scheduleId);
            entry.autoCloseTimer = setTimeout(() => {
                entry.autoCloseTimer = null;
                setAutoCloseScheduleId(null);
                useChatStore.getState().close();
            }, CHAT_AUTO_CLOSE_AFTER_COMPLETE_MS);
        },
        [clearAllAutoCloseTimers],
    );

    const acceptAutoClose = useCallback((): void => {
        clearAllAutoCloseTimers();
        useChatStore.getState().close();
    }, [clearAllAutoCloseTimers]);

    const cancelAutoClose = clearAllAutoCloseTimers;

    const updateThreadState = useCallback(
        (threadId: string, update: (state: ThreadStreamState) => ThreadStreamState): void => {
            setStatesByThreadId((previous) => ({
                ...previous,
                [threadId]: update(previous[threadId] ?? INITIAL_THREAD_STREAM_STATE),
            }));
        },
        [],
    );

    const ensurePendingMessageForEntry = useCallback(
        (threadId: string, entry: StreamEntry): string => {
            const existing = entry.optimisticMessageId;
            if (!is.null(existing)) return existing;
            const messageId = generateLocalMessageId();
            entry.optimisticMessageId = messageId;
            updateMessages(threadId, (data) =>
                prependPendingAssistantMessage(data, messageId, new Date().toISOString()),
            );
            return messageId;
        },
        [updateMessages],
    );

    // A resumed turn can continue an existing assistant message without a new
    // message-start (e.g. after a HITL answer). The answer-time placeholder keeps
    // the working feedback visible meanwhile, but it must yield the latest-assistant
    // slot the moment an input-requesting block lands on the real message — pinned
    // below it, the placeholder would suppress that block's Accept/Reject controls.
    const absorbPlaceholderOnActivity = useCallback(
        (threadId: string, entry: StreamEntry): void => {
            const optimisticMessageId = entry.optimisticMessageId;
            if (is.null(optimisticMessageId) || optimisticMessageId === entry.currentMessageId) return;
            entry.optimisticMessageId = null;
            updateMessages(threadId, (data) => removeAssistantMessage(data, optimisticMessageId));
        },
        [updateMessages],
    );

    // An optimistic placeholder that was never reconciled with a real message must
    // not outlive the turn — left behind it renders as a forever-pending spinner.
    const dropStaleOptimisticMessage = useCallback(
        (threadId: string, entry: StreamEntry): void => {
            const optimisticMessageId = entry.optimisticMessageId;
            entry.optimisticMessageId = null;
            if (is.null(optimisticMessageId) || optimisticMessageId === entry.currentMessageId) return;
            updateMessages(threadId, (data) => removeAssistantMessage(data, optimisticMessageId));
        },
        [updateMessages],
    );

    const markStreamClosed = useCallback(
        (threadId: string, entry: StreamEntry): void => {
            clearReconnectTimer(entry);
            entry.stream = null;
            entry.reconnectAttempts = 0;
            // A closed stream voids any pending regenerate resolution. The complete
            // handler captures it beforehand; errored or aborted streams — which must
            // never auto-close the chat — simply lose it here.
            entry.sawLiveContextRefresh = false;
            updateThreadState(threadId, (state) => ({ ...state, hasLiveStream: false }));
        },
        [clearReconnectTimer, updateThreadState],
    );

    const openRef = useRef<(threadId: string, body: UserMessage) => void>(() => undefined);
    const startTurnRef = useRef<(threadId: string, body: UserMessage, messageId?: string | null) => void>(noop);

    const scheduleReconnect = useCallback(
        (threadId: string, entry: StreamEntry): void => {
            const attempt = entry.reconnectAttempts + 1;
            if (attempt > MAX_RECONNECT_ATTEMPTS) {
                const messageId = entry.currentMessageId;
                if (!is.null(messageId)) {
                    updateMessages(threadId, (data) => setAssistantMessageStatus(data, messageId, 'error'));
                }
                // Giving up ends the turn: a late close from the same failed request must not
                // start a fresh reconnect cycle (markStreamClosed resets the attempt count).
                entry.isComplete = true;
                entry.optimisticMessageId = null;
                markStreamClosed(threadId, entry);
                updateThreadState(threadId, (state) => ({
                    ...state,
                    errorReason: state.errorReason ?? 'connection-lost',
                    status: 'error',
                }));
                reconcileThreadAndList(threadId);
                return;
            }
            entry.reconnectAttempts = attempt;
            clearReconnectTimer(entry);
            entry.reconnectTimer = setTimeout(
                () => {
                    entry.reconnectTimer = null;
                    openRef.current(threadId, { resume: true, type: 'resume' });
                },
                RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1),
            );
        },
        [clearReconnectTimer, markStreamClosed, reconcileThreadAndList, updateMessages, updateThreadState],
    );

    const scheduleBusyResume = useCallback((threadId: string, entry: StreamEntry, delayMs: number): void => {
        entry.busyRetryTimer = setTimeout(() => {
            entry.busyRetryTimer = null;
            // Lifting the hold first keeps an early-firing timer from being swallowed by the gate.
            entry.busyUntil = null;
            startTurnRef.current(threadId, { resume: true, type: 'resume' }, entry.currentMessageId);
        }, delayMs);
    }, []);

    const deferResumeWhileBusy = useCallback(
        (threadId: string, entry: StreamEntry, messageId: string | null): boolean => {
            if (is.null(entry.busyUntil) || Date.now() >= entry.busyUntil) return false;
            if (!is.null(messageId)) entry.currentMessageId = messageId;
            if (is.null(entry.busyRetryTimer)) scheduleBusyResume(threadId, entry, entry.busyUntil - Date.now());
            return true;
        },
        [scheduleBusyResume],
    );

    const holdBusyTurn = useCallback(
        (threadId: string, entry: StreamEntry, rejection: ChatStreamRejectedError, wasResume: boolean): void => {
            if (wasResume) entry.busyStreak += 1;
            const delayMs = busyRetryDelayMs(rejection.retryAfterSeconds, entry.busyStreak);
            entry.busyUntil = Date.now() + delayMs;
            if (wasResume) scheduleBusyResume(threadId, entry, delayMs);
        },
        [scheduleBusyResume],
    );

    const settleRejectedMessages = useCallback(
        (threadId: string, entry: StreamEntry, isBusy: boolean, wasResume: boolean): void => {
            const messageId = entry.currentMessageId;
            const placeholderId = entry.optimisticMessageId;
            if (isBusy && !is.null(placeholderId)) {
                entry.optimisticMessageId = null;
                // A busy resume retries itself, but a new request turned away before it started keeps its
                // reply as an error, so it can be retried.
                if (!wasResume && entry.currentMessageId === placeholderId) {
                    updateMessages(threadId, (data) => setAssistantMessageStatus(data, placeholderId, 'error'));
                    return;
                }
                if (entry.currentMessageId === placeholderId) entry.currentMessageId = null;
                updateMessages(threadId, (data) => removeAssistantMessage(data, placeholderId));
                return;
            }
            dropStaleOptimisticMessage(threadId, entry);
            if (!isBusy && !is.null(messageId)) {
                updateMessages(threadId, (data) => setAssistantMessageStatus(data, messageId, 'error'));
            }
        },
        [dropStaleOptimisticMessage, updateMessages],
    );

    // A rejected request is an answer, not a dropped stream: retrying it only repeats the answer.
    // On 429 another stream still owns the turn, so its message stays pending and resumes wait
    // out Retry-After instead of hammering the run lock. A rejected resume is retried once the
    // lock should be free; without a readable Retry-After the fallback delay backs off per answer.
    const rejectTurn = useCallback(
        (threadId: string, entry: StreamEntry, rejection: ChatStreamRejectedError, wasResume: boolean): void => {
            const isBusy = rejection.status === HTTP_TOO_MANY_REQUESTS;
            entry.isComplete = true;
            clearBusyRetryTimer(entry);
            entry.busyUntil = null;
            if (isBusy) {
                holdBusyTurn(threadId, entry, rejection, wasResume);
            } else {
                entry.busyStreak = 0;
            }
            settleRejectedMessages(threadId, entry, isBusy, wasResume);
            markStreamClosed(threadId, entry);
            updateThreadState(threadId, (state) => ({
                ...state,
                errorReason: isBusy ? 'busy' : 'rejected',
                status: 'error',
            }));
            reconcileThreadAndList(threadId);
        },
        [
            clearBusyRetryTimer,
            holdBusyTurn,
            markStreamClosed,
            reconcileThreadAndList,
            settleRejectedMessages,
            updateThreadState,
        ],
    );

    const failTurnFromServer = useCallback(
        (threadId: string, entry: StreamEntry, eventId: string): void => {
            entry.lastEventId = eventId;
            entry.reconnectAttempts = 0;
            entry.isComplete = true;
            dropStaleOptimisticMessage(threadId, entry);
            const messageId = entry.currentMessageId;
            if (!is.null(messageId)) {
                updateMessages(threadId, (data) => setAssistantMessageStatus(data, messageId, 'error'));
            }
            markStreamClosed(threadId, entry);
            updateThreadState(threadId, (state) => ({
                ...state,
                errorReason: 'server',
                status: 'error',
            }));
        },
        [dropStaleOptimisticMessage, markStreamClosed, updateMessages, updateThreadState],
    );

    const open = useCallback(
        (threadId: string, body: UserMessage): void => {
            const entry = getOrCreateEntry(threadId);
            entry.stream?.stop();
            entry.lastRequest = body;
            const stream: ChatStream = send(
                {
                    body,
                    lastEventId: is.null(entry.lastEventId) ? undefined : entry.lastEventId,
                    threadId,
                },
                {
                    chunk: (text) => {
                        entry.reconnectAttempts = 0;
                        const messageId = entry.currentMessageId;
                        if (is.null(messageId)) return;
                        updateMessages(threadId, (data) => appendTextChunk(data, messageId, text));
                    },
                    complete: (event) => {
                        clearBusyRetryTimer(entry);
                        entry.lastEventId = event.eventId;
                        entry.reconnectAttempts = 0;
                        entry.isComplete = true;
                        dropStaleOptimisticMessage(threadId, entry);
                        const messageId = entry.currentMessageId ?? event.messageId;
                        updateMessages(threadId, (data) => setAssistantMessageStatus(data, messageId, 'complete'));
                        // An agent-action turn whose stream delivered a context refresh has
                        // resolved — the only case that schedules the auto-close, captured
                        // before markStreamClosed voids the marker.
                        const shouldScheduleAutoClose = entry.isAgentActionTurn && entry.sawLiveContextRefresh;
                        markStreamClosed(threadId, entry);
                        updateThreadState(threadId, (state) => ({ ...state, status: 'complete' }));
                        if (
                            shouldScheduleAutoClose &&
                            activeThreadIdRef.current === threadId &&
                            useChatStore.getState().isOpen
                        ) {
                            scheduleAutoClose(entry);
                        }
                    },
                    error: (event) => {
                        if (event instanceof ChatStreamRejectedError) {
                            if (entry.stream !== stream) return;
                            rejectTurn(threadId, entry, event, body.type === 'resume');
                            return;
                        }
                        if (event instanceof Error) {
                            // Only the entry's current stream may drive reconnects; a superseded
                            // or abandoned request reporting late would start a parallel cycle.
                            if (entry.isComplete || entry.stream !== stream) return;
                            scheduleReconnect(threadId, entry);
                            return;
                        }
                        failTurnFromServer(threadId, entry, event.eventId);
                    },
                    messageStart: (event) => {
                        entry.lastEventId = event.eventId;
                        entry.reconnectAttempts = 0;
                        entry.busyUntil = null;
                        entry.busyStreak = 0;
                        const optimisticMessageId = entry.optimisticMessageId;
                        const previousMessageId = entry.currentMessageId;
                        entry.currentMessageId = event.messageId;
                        entry.optimisticMessageId = null;
                        // A new message-start ends the previous in-flight message; without this
                        // it would stay pending (spinning) until the next server refetch.
                        if (
                            !is.null(previousMessageId) &&
                            previousMessageId !== event.messageId &&
                            previousMessageId !== optimisticMessageId
                        ) {
                            updateMessages(threadId, (data) =>
                                setAssistantMessageStatus(data, previousMessageId, 'complete'),
                            );
                        }
                        updateMessages(threadId, (data) =>
                            reconcileAssistantMessageStart(data, event.messageId, event.timestamp, optimisticMessageId),
                        );
                        updateThreadState(threadId, (state) => ({ ...state, messageId: event.messageId }));
                    },
                    structuredResponse: (event) => {
                        entry.lastEventId = event.eventId;
                        entry.reconnectAttempts = 0;
                        const messageId = entry.currentMessageId;
                        if (is.null(messageId)) return;
                        if (event.payload.blocks.some(requestsInputNow)) {
                            absorbPlaceholderOnActivity(threadId, entry);
                        }
                        updateMessages(threadId, (data) => appendStructuredEvent(data, messageId, event));
                    },
                    contextRefresh: (event) => {
                        entry.lastEventId = event.eventId;
                        entry.reconnectAttempts = 0;
                        const messageId = entry.currentMessageId;
                        if (is.null(messageId)) return;
                        entry.sawLiveContextRefresh = true;
                        updateMessages(threadId, (data) => appendContextRefreshEvent(data, messageId, event));
                    },
                },
            );
            entry.stream = stream;
        },
        [
            absorbPlaceholderOnActivity,
            clearBusyRetryTimer,
            dropStaleOptimisticMessage,
            failTurnFromServer,
            getOrCreateEntry,
            markStreamClosed,
            rejectTurn,
            scheduleAutoClose,
            scheduleReconnect,
            send,
            updateMessages,
            updateThreadState,
        ],
    );

    openRef.current = open;

    const startTurn = useCallback(
        (threadId: string, body: UserMessage, messageId: string | null = null): void => {
            const entry = getOrCreateEntry(threadId);
            // Dedupe on the entry refs, not on React state: state lags a synchronous
            // stopTurn() and would block an immediate restart of the same thread.
            if (!is.null(entry.stream) || !is.null(entry.reconnectTimer)) return;
            if (body.type === 'resume' && deferResumeWhileBusy(threadId, entry, messageId)) return;
            clearBusyRetryTimer(entry);
            clearReconnectTimer(entry);
            clearAllAutoCloseTimers();
            // A resume continues the same logical turn (the apply-stream after a HITL
            // answer) and a retry repeats it, so the marker survives both; any new turn resets it.
            if (body.type !== 'resume') entry.sawLiveContextRefresh = false;
            if (body.type !== 'resume' && body.type !== 'retry') {
                entry.isAgentActionTurn = body.type === 'regenerate' || body.type === 'create-jsa-entities';
            }
            // A known pending message (resume) attaches directly; otherwise an optimistic
            // placeholder makes the assistant reply visible before the stream reports in.
            const currentMessageId = is.null(messageId) ? ensurePendingMessageForEntry(threadId, entry) : messageId;
            entry.currentMessageId = currentMessageId;
            entry.isComplete = false;
            entry.lastEventId = null;
            entry.reconnectAttempts = 0;
            updateThreadState(threadId, () => ({
                errorReason: null,
                hasLiveStream: true,
                messageId: currentMessageId,
                status: 'streaming',
            }));
            open(threadId, body);
        },
        [
            clearAllAutoCloseTimers,
            clearBusyRetryTimer,
            clearReconnectTimer,
            ensurePendingMessageForEntry,
            getOrCreateEntry,
            deferResumeWhileBusy,
            open,
            updateThreadState,
        ],
    );

    startTurnRef.current = startTurn;

    const retryTurn = useCallback(
        (threadId: string, failedMessageId: string): void => {
            const entry = getOrCreateEntry(threadId);
            if (!is.null(entry.stream) || !is.null(entry.reconnectTimer)) return;
            updateMessages(threadId, (data) => removeAssistantMessage(data, failedMessageId));
            if (entry.currentMessageId === failedMessageId) entry.currentMessageId = null;
            if (entry.optimisticMessageId === failedMessageId) entry.optimisticMessageId = null;
            startTurn(threadId, buildRetryRequest(statesByThreadId[threadId]?.errorReason ?? null, entry.lastRequest));
        },
        [getOrCreateEntry, startTurn, statesByThreadId, updateMessages],
    );

    const ensurePendingMessage = useCallback(
        (threadId: string, settleMessageId: string | null = null): string => {
            const entry = getOrCreateEntry(threadId);
            const messageId = ensurePendingMessageForEntry(threadId, entry);
            // Answering settles the message that asked: left pending, its activity card
            // would keep spinning next to the fresh placeholder until message-start.
            if (!is.null(settleMessageId) && settleMessageId !== messageId) {
                updateMessages(threadId, (data) => setAssistantMessageStatus(data, settleMessageId, 'complete'));
            }
            updateThreadState(threadId, (state) => ({
                ...state,
                errorReason: null,
                messageId: state.messageId ?? messageId,
                status: 'streaming',
            }));
            return messageId;
        },
        [ensurePendingMessageForEntry, getOrCreateEntry, updateMessages, updateThreadState],
    );

    const rollbackPendingMessage = useCallback(
        (
            threadId: string,
            messageId: string,
            previousTurn: ChatTurnState,
            unsettleMessageId: string | null = null,
        ): void => {
            const entry = entriesRef.current.get(threadId);
            if (!is.undefined(entry)) {
                if (entry.optimisticMessageId === messageId) entry.optimisticMessageId = null;
                if (entry.currentMessageId === messageId) entry.currentMessageId = null;
            }
            updateMessages(threadId, (data) => removeAssistantMessage(data, messageId));
            if (!is.null(unsettleMessageId)) {
                updateMessages(threadId, (data) => setAssistantMessageStatus(data, unsettleMessageId, 'pending'));
            }
            updateThreadState(threadId, (state) =>
                state.hasLiveStream ? state : { ...previousTurn, hasLiveStream: false },
            );
        },
        [updateMessages, updateThreadState],
    );

    const appendUserMessage = useCallback(
        (threadId: string, message: AgentChatMessage): void => {
            updateMessages(threadId, (data) => prependUserMessage(data, message));
        },
        [updateMessages],
    );

    // Ending a turn here does not reconcile the thread or the list: the caller owns that, so
    // restarting the same turn (a HITL answer) does not fire a refetch it is about to repeat.
    const stopTurn = useCallback(
        (threadId: string | null): boolean => {
            if (is.null(threadId)) return false;
            const entry = entriesRef.current.get(threadId);
            if (is.undefined(entry)) return false;
            // Entries outlive their turn, so a live turn is one that neither finished nor gave
            // up: still streaming, or waiting on a reconnect that this call is about to cancel.
            const wasLive =
                !is.null(entry.busyRetryTimer) ||
                (!entry.isComplete && (!is.null(entry.stream) || !is.null(entry.reconnectTimer)));
            clearBusyRetryTimer(entry);
            clearReconnectTimer(entry);
            clearAllAutoCloseTimers();
            entry.isComplete = true;
            entry.stream?.stop();
            entry.stream = null;
            entry.reconnectAttempts = 0;
            entry.busyStreak = 0;
            const optimisticMessageId = entry.optimisticMessageId;
            if (!is.null(optimisticMessageId)) {
                entry.optimisticMessageId = null;
                if (entry.currentMessageId === optimisticMessageId) entry.currentMessageId = null;
                updateMessages(threadId, (data) => removeAssistantMessage(data, optimisticMessageId));
            }
            updateThreadState(threadId, () => INITIAL_THREAD_STREAM_STATE);

            return wasLive;
        },
        [clearAllAutoCloseTimers, clearBusyRetryTimer, clearReconnectTimer, updateMessages, updateThreadState],
    );

    const clearTurn = useCallback(
        (threadId: string | null): void => {
            if (is.null(threadId)) return;
            updateThreadState(threadId, (state) => ({
                ...INITIAL_THREAD_STREAM_STATE,
                hasLiveStream: state.hasLiveStream,
                status: state.hasLiveStream ? 'streaming' : 'idle',
            }));
        },
        [updateThreadState],
    );

    const getTurn = useCallback(
        (threadId: string | null): ChatTurnState => {
            if (is.null(threadId)) return INITIAL_TURN;
            const state = statesByThreadId[threadId];
            if (is.undefined(state)) return INITIAL_TURN;
            return {
                errorReason: state.errorReason,
                messageId: state.messageId,
                status: state.status,
            };
        },
        [statesByThreadId],
    );

    const hasLiveStream = useCallback(
        (threadId: string | null): boolean => {
            if (is.null(threadId)) return false;
            return statesByThreadId[threadId]?.hasLiveStream === true;
        },
        [statesByThreadId],
    );

    useEffect(
        () => () => {
            for (const entry of entriesRef.current.values()) {
                clearBusyRetryTimer(entry);
                clearReconnectTimer(entry);
                clearAutoCloseTimer(entry);
                entry.stream?.stop();
            }
            entriesRef.current.clear();
        },
        [clearAutoCloseTimer, clearBusyRetryTimer, clearReconnectTimer],
    );

    return useMemo(
        () => ({
            acceptAutoClose,
            autoCloseScheduleId,
            appendUserMessage,
            cancelAutoClose,
            clearTurn,
            ensurePendingMessage,
            getTurn,
            hasLiveStream,
            isAutoClosePending,
            keepChatOpen: cancelAutoClose,
            retryTurn,
            rollbackPendingMessage,
            startTurn,
            stopTurn,
        }),
        [
            acceptAutoClose,
            autoCloseScheduleId,
            appendUserMessage,
            cancelAutoClose,
            clearTurn,
            ensurePendingMessage,
            getTurn,
            hasLiveStream,
            isAutoClosePending,
            retryTurn,
            rollbackPendingMessage,
            startTurn,
            stopTurn,
        ],
    );
};
